<template>
    <div class="system-dashboard">
      <v-container fluid>
        <!-- Loading-Indicator -->
        <v-skeleton-loader v-if="props.loading" type="card" />

        <!-- Fehleranzeige -->
        <v-alert v-else-if="!props.systemInfo" type="error" text>
          Systeminformationen konnten nicht geladen werden.
        </v-alert>

        <!-- Systeminformationen -->
        <template v-else>
          <div class="d-flex justify-space-between align-center mb-4">
            <h1 class="text-h4">Systemstatus</h1>
            <v-btn icon="mdi-refresh" @click="$emit('refresh')" :loading="props.loading"></v-btn>
          </div>

          <v-row>
            <!-- PHP-Umgebung -->
            <v-col cols="12" md="6">
              <v-card class="mb-4">
                <v-card-title class="d-flex align-center">
                  <v-icon color="primary" class="me-2">mdi-language-php</v-icon>
                  PHP Umgebung
                </v-card-title>
                <v-card-text>
                  <v-list>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon icon="mdi-language-php"></v-icon>
                      </template>
                      <v-list-item-title>PHP Version</v-list-item-title>
                      <v-list-item-subtitle>{{ props.systemInfo.php?.version || 'Unbekannt' }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon icon="mdi-memory"></v-icon>
                      </template>
                      <v-list-item-title>Arbeitsspeicher-Limit</v-list-item-title>
                      <v-list-item-subtitle>{{ props.systemInfo.php?.memory_limit || 'Unbekannt' }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon icon="mdi-timer-outline"></v-icon>
                      </template>
                      <v-list-item-title>Max. Ausführungszeit</v-list-item-title>
                      <v-list-item-subtitle>{{ props.systemInfo.php?.max_execution_time || 'Unbekannt' }}s</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon icon="mdi-upload"></v-icon>
                      </template>
                      <v-list-item-title>Upload-Limit</v-list-item-title>
                      <v-list-item-subtitle>{{ props.systemInfo.php?.upload_max_filesize || 'Unbekannt' }}</v-list-item-subtitle>
                    </v-list-item>
                  </v-list>

                  <!-- PHP-Extensions -->
                  <div class="mt-4">
                    <div class="d-flex align-center mb-2">
                      <v-icon class="me-2" icon="mdi-puzzle"></v-icon>
                      <h3 class="text-h6">Installierte Erweiterungen</h3>
                    </div>
                    <v-chip-group>
                      <v-chip v-for="ext in props.systemInfo.php?.extensions" :key="ext" size="small" color="primary" variant="outlined">
                        {{ ext }}
                      </v-chip>
                    </v-chip-group>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Datenbank -->
            <v-col cols="12" md="6">
              <v-card class="mb-4">
                <v-card-title class="d-flex align-center">
                  <v-icon color="primary" class="me-2">mdi-database</v-icon>
                  Datenbank
                </v-card-title>
                <v-card-text>
                  <v-list>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon icon="mdi-database-outline"></v-icon>
                      </template>
                      <v-list-item-title>Typ</v-list-item-title>
                      <v-list-item-subtitle>{{ props.systemInfo.database?.type || 'Unbekannt' }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon icon="mdi-tag-outline"></v-icon>
                      </template>
                      <v-list-item-title>Version</v-list-item-title>
                      <v-list-item-subtitle>{{ props.systemInfo.database?.version || 'Unbekannt' }}</v-list-item-subtitle>
                    </v-list-item>
                  </v-list>

                  <!-- Tabellen -->
                  <div class="mt-4" v-if="props.systemInfo.database?.tables">
                    <div class="d-flex align-center mb-2">
                      <v-icon class="me-2" icon="mdi-table"></v-icon>
                      <h3 class="text-h6">Tabellen ({{ Object.keys(props.systemInfo.database.tables).length }})</h3>
                    </div>
                    <v-table density="compact" class="table-fixed">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th class="text-right">Datensätze</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(info, table) in props.systemInfo.database.tables" :key="table">
                          <td>{{ table }}</td>
                          <td class="text-right">{{ info.count }}</td>
                        </tr>
                      </tbody>
                    </v-table>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Server-Info -->
            <v-col cols="12" md="6">
              <v-card>
                <v-card-title class="d-flex align-center">
                  <v-icon color="primary" class="me-2">mdi-server</v-icon>
                  Server
                </v-card-title>
                <v-card-text>
                  <v-list>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon icon="mdi-server"></v-icon>
                      </template>
                      <v-list-item-title>Software</v-list-item-title>
                      <v-list-item-subtitle>{{ props.systemInfo.server?.software || 'Unbekannt' }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon icon="mdi-protocol"></v-icon>
                      </template>
                      <v-list-item-title>Protokoll</v-list-item-title>
                      <v-list-item-subtitle>{{ props.systemInfo.server?.protocol || 'Unbekannt' }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon icon="mdi-desktop-tower"></v-icon>
                      </template>
                      <v-list-item-title>Hostname</v-list-item-title>
                      <v-list-item-subtitle>{{ props.systemInfo.server?.hostname || 'Unbekannt' }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon icon="mdi-linux"></v-icon>
                      </template>
                      <v-list-item-title>Betriebssystem</v-list-item-title>
                      <v-list-item-subtitle>{{ props.systemInfo.server?.os || 'Unbekannt' }}</v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- App-Info -->
            <v-col cols="12" md="6">
              <v-card>
                <v-card-title class="d-flex align-center">
                  <v-icon color="primary" class="me-2">mdi-application</v-icon>
                  Anwendung
                </v-card-title>
                <v-card-text>
                  <v-list>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon icon="mdi-cog"></v-icon>
                      </template>
                      <v-list-item-title>Umgebung</v-list-item-title>
                      <v-list-item-subtitle>{{ props.systemInfo.application?.environment || 'Unbekannt' }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon icon="mdi-bug"></v-icon>
                      </template>
                      <v-list-item-title>Debug-Modus</v-list-item-title>
                      <v-list-item-subtitle>
                        <v-chip color="warning" size="small" v-if="props.systemInfo.application?.debug_mode">Aktiv</v-chip>
                        <v-chip color="success" size="small" v-else>Inaktiv</v-chip>
                      </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon icon="mdi-web"></v-icon>
                      </template>
                      <v-list-item-title>CORS-Ursprung</v-list-item-title>
                      <v-list-item-subtitle>{{ props.systemInfo.application?.cors_origin || 'Unbekannt' }}</v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- System-Ressourcen (falls verfügbar) -->
            <v-col cols="12" v-if="props.systemInfo.resources">
              <v-card>
                <v-card-title class="d-flex align-center">
                  <v-icon color="primary" class="me-2">mdi-chart-areaspline</v-icon>
                  Ressourcen-Nutzung
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="4">
                      <h3 class="text-h6 mb-2">CPU-Auslastung</h3>
                      <v-progress-circular
                        :model-value="props.systemInfo.resources?.cpu_usage || 0"
                        :color="getResourceColor(props.systemInfo.resources?.cpu_usage || 0)"
                        size="100"
                        width="12"
                      >
                        {{ props.systemInfo.resources?.cpu_usage || 0 }}%
                      </v-progress-circular>
                    </v-col>
                    <v-col cols="12" md="4">
                      <h3 class="text-h6 mb-2">RAM-Nutzung</h3>
                      <v-progress-linear
                        :model-value="props.systemInfo.resources?.memory_usage || 0"
                        :color="getResourceColor(props.systemInfo.resources?.memory_usage || 0)"
                        height="25"
                      >
                        <template v-slot:default="{ value }">
                          <strong>{{ Math.ceil(value) }}%</strong>
                        </template>
                      </v-progress-linear>
                      <div class="mt-2 text-caption">
                        {{ formatBytes(props.systemInfo.resources?.memory_used || 0) }} /
                        {{ formatBytes(props.systemInfo.resources?.memory_total || 0) }}
                      </div>
                    </v-col>
                    <v-col cols="12" md="4">
                      <h3 class="text-h6 mb-2">Festplatten-Nutzung</h3>
                      <v-progress-linear
                        :model-value="props.systemInfo.resources?.disk_usage || 0"
                        :color="getResourceColor(props.systemInfo.resources?.disk_usage || 0)"
                        height="25"
                      >
                        <template v-slot:default="{ value }">
                          <strong>{{ Math.ceil(value) }}%</strong>
                        </template>
                      </v-progress-linear>
                      <div class="mt-2 text-caption">
                        {{ formatBytes(props.systemInfo.resources?.disk_used || 0) }} /
                        {{ formatBytes(props.systemInfo.resources?.disk_total || 0) }}
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Umgebungsvariablen -->
            <v-col cols="12" v-if="props.systemInfo.env && Object.keys(props.systemInfo.env).length > 0">
              <v-card>
                <v-card-title class="d-flex align-center">
                  <v-icon color="primary" class="me-2">mdi-variable</v-icon>
                  Umgebungsvariablen
                </v-card-title>
                <v-card-text>
                  <v-expansion-panels>
                    <v-expansion-panel>
                      <v-expansion-panel-title>
                        <div class="d-flex align-center">
                          <v-icon class="me-2">mdi-eye-off</v-icon>
                          Umgebungsvariablen anzeigen
                        </div>
                      </v-expansion-panel-title>
                      <v-expansion-panel-text>
                        <v-table density="compact">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Wert</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(value, key) in props.systemInfo.env" :key="key">
                              <td><code>{{ key }}</code></td>
                              <td>{{ isSensitive(key) ? '******' : value }}</td>
                            </tr>
                          </tbody>
                        </v-table>
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </v-container>
    </div>
  </template>

  <script setup>

   
  // Props für die übergebenen Daten
  const props = defineProps({
    systemInfo: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    }
  });

  // Events für die Komponente definieren
   

  // Hilfsfunktion: Farbe basierend auf Ressourcennutzung bestimmen
  function getResourceColor(value) {
    if (value < 60) return 'success';
    if (value < 85) return 'warning';
    return 'error';
  }

  // Hilfsfunktion: Bytes in lesbare Größen formatieren
  function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  // Prüft, ob eine Umgebungsvariable sensible Daten enthält
  function isSensitive(key) {
    const sensitiveKeys = ['key', 'token', 'secret', 'password', 'pass', 'pwd', 'auth', 'credential'];
    return sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive.toLowerCase()));
  }
  </script>

  <style scoped>
  .system-dashboard {
    padding: 16px;
  }
  .table-fixed {
    max-height: 300px;
    overflow-y: auto;
  }
  .v-card {
    height: 100%;
  }
  </style>
